import { useState, useEffect } from "react";
import { 
  Image, Plus, Edit2, Trash2, X, Eye, EyeOff, Upload, GripVertical 
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const GalleriesTab = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    cover_image_url: "",
    images: [],
    is_visible: true
  });
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newImageCaption, setNewImageCaption] = useState("");

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const res = await axios.get(`${API}/galleries?visible_only=false`);
      setGalleries(res.data || []);
    } catch (err) {
      console.error("Error fetching galleries:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGallery) {
        await axios.put(`${API}/galleries/${editingGallery.id}`, formData);
      } else {
        await axios.post(`${API}/galleries`, formData);
      }
      fetchGalleries();
      closeModal();
    } catch (err) {
      console.error("Error saving gallery:", err);
      alert("Error saving gallery");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this gallery?")) return;
    try {
      await axios.delete(`${API}/galleries/${id}`);
      fetchGalleries();
    } catch (err) {
      console.error("Error deleting gallery:", err);
    }
  };

  const toggleVisibility = async (gallery) => {
    try {
      await axios.put(`${API}/galleries/${gallery.id}`, {
        is_visible: !gallery.is_visible
      });
      fetchGalleries();
    } catch (err) {
      console.error("Error toggling visibility:", err);
    }
  };

  const openModal = (gallery = null) => {
    if (gallery) {
      setEditingGallery(gallery);
      setFormData({
        title: gallery.title || "",
        category: gallery.category || "",
        cover_image_url: gallery.cover_image_url || "",
        images: gallery.images || [],
        is_visible: gallery.is_visible ?? true
      });
    } else {
      setEditingGallery(null);
      setFormData({
        title: "",
        category: "",
        cover_image_url: "",
        images: [],
        is_visible: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGallery(null);
    setNewImageUrl("");
    setNewImageCaption("");
  };

  const addImage = () => {
    if (!newImageUrl.trim()) return;
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, { url: newImageUrl, caption: newImageCaption }]
    }));
    setNewImageUrl("");
    setNewImageCaption("");
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const categories = ["Football", "Basketball", "Baseball", "Soccer", "Volleyball", "Track", "Tennis", "Swimming", "Wrestling", "Other"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="galleries-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Photo Galleries</h2>
          <p className="text-zinc-500 text-sm mt-1">Manage photo galleries for the homepage</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Gallery
        </button>
      </div>

      {/* Galleries Grid */}
      {galleries.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/5 p-12 text-center">
          <Image className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-white text-lg mb-2">No Galleries Yet</h3>
          <p className="text-zinc-500 text-sm mb-4">Create your first photo gallery</p>
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400"
          >
            Create Gallery
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleries.map((gallery) => (
            <div
              key={gallery.id}
              className={`bg-zinc-900/50 border overflow-hidden ${
                gallery.is_visible ? "border-white/10" : "border-red-500/30"
              }`}
            >
              {/* Cover Image */}
              <div className="aspect-video bg-zinc-800 relative">
                {gallery.cover_image_url ? (
                  <img
                    src={gallery.cover_image_url}
                    alt={gallery.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-12 h-12 text-zinc-700" />
                  </div>
                )}
                {!gallery.is_visible && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-red-500/90 text-white text-xs font-bold">
                    Hidden
                  </div>
                )}
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs">
                  {gallery.images?.length || 0} photos
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                {gallery.category && (
                  <span className="text-orange-500 text-xs font-bold uppercase tracking-wider">
                    {gallery.category}
                  </span>
                )}
                <h3 className="text-white font-bold mt-1 line-clamp-1">{gallery.title}</h3>
              </div>

              {/* Actions */}
              <div className="border-t border-white/5 p-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => toggleVisibility(gallery)}
                  className={`p-2 transition-colors ${
                    gallery.is_visible
                      ? "text-green-500 hover:bg-green-500/10"
                      : "text-red-500 hover:bg-red-500/10"
                  }`}
                  title={gallery.is_visible ? "Hide" : "Show"}
                >
                  {gallery.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => openModal(gallery)}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(gallery.id)}
                  className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-zinc-900">
              <h3 className="text-xl font-bold text-white">
                {editingGallery ? "Edit Gallery" : "New Gallery"}
              </h3>
              <button onClick={closeModal} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Gallery Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="e.g. Championship Game 2024"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Cover Image URL */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Cover Image URL
                </label>
                <input
                  type="url"
                  value={formData.cover_image_url}
                  onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://..."
                />
                {formData.cover_image_url && (
                  <img
                    src={formData.cover_image_url}
                    alt="Cover preview"
                    className="mt-2 h-32 object-cover"
                  />
                )}
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Gallery Images
                </label>
                
                {/* Add New Image */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    className="flex-1 px-4 py-2 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none text-sm"
                    placeholder="Image URL"
                  />
                  <input
                    type="text"
                    value={newImageCaption}
                    onChange={(e) => setNewImageCaption(e.target.value)}
                    className="w-40 px-4 py-2 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none text-sm"
                    placeholder="Caption (optional)"
                  />
                  <button
                    type="button"
                    onClick={addImage}
                    className="px-4 py-2 bg-zinc-800 text-white hover:bg-zinc-700"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Image List */}
                {formData.images.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-black/50 p-2 border border-white/5">
                        <img src={img.url} alt="" className="w-16 h-12 object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{img.url}</p>
                          {img.caption && (
                            <p className="text-zinc-500 text-xs">{img.caption}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="p-1 text-red-500 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_visible"
                  checked={formData.is_visible}
                  onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                  className="w-5 h-5"
                />
                <label htmlFor="is_visible" className="text-white">
                  Visible on site
                </label>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-500 text-black font-bold hover:bg-orange-400"
                >
                  {editingGallery ? "Update" : "Create"} Gallery
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleriesTab;
